// ============================================
// FIRM TEAM (Firm › Team)
// ============================================
// The firm admin's people screen: everyone in the firm with their roles,
// specialization, assigned clients and standing; add a person (invited by
// email, or with a password set now); edit name, specialization, clients and
// active/inactive; resend an invitation; remove a person. Role changes stay
// a batch (tick, then Save Changes) through PATCH /firms/:firmId/users/roles;
// the rest is the D2 restore (RESTORE_PLAN R3.1) on the routes it lacked.

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { PasswordInput } from './ui/password-input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Shield, Users, Save, Loader2, CheckCircle2, AlertTriangle, UserPlus, Pencil, Trash2, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { projectId } from '../utils/supabase/info';
import { getAuthHeaders } from '../utils/apiHeaders';

export type FirmMemberStatus = 'invited' | 'inactive' | 'active';

export interface Consultant {
  id: string;
  email: string;
  name?: string;
  roles: string[];
  consultantType?: string | null;
  assignedCompanies?: string[];
  status?: FirmMemberStatus;
  invitedAt?: string | null;
  createdAt: string;
}

interface FirmRoleManagerProps {
  firmId: string;
  accessToken: string;
  currentUser: any;
}

// Available roles for consultants
const CONSULTANT_ROLES = [
  { value: 'consultant', label: 'Consultant', description: 'Standard consultant access' },
  { value: 'cfAdmin', label: 'Firm Admin', description: 'Manages the consulting firm' },
  { value: 'bdRep', label: 'Business Development Rep', description: 'Pre-sales, proposals and client acquisition' },
  { value: 'billingAdmin', label: 'Billing Admin', description: 'Manage firm billing & subscriptions' },
];

/** Specializations (the server's CONSULTANT_TYPES vocabulary). */
export const CONSULTANT_TYPES: { value: string; label: string; blurb: string }[] = [
  { value: 'none', label: 'None (generalist)', blurb: 'Can take any kind of work' },
  { value: 'bdm', label: 'Business Development Manager', blurb: 'Sales, intake and client development' },
  { value: 'engagement-lead', label: 'Firm Engagement Lead', blurb: 'Owns client delivery and the project' },
  { value: 'functional', label: 'Functional Consultant', blurb: 'Workflows, request types and business processes' },
  { value: 'technical-architect', label: 'Technical/Solution Architect', blurb: 'Integrations and complex technical configuration' },
];

export function consultantTypeLabel(type: string | null | undefined): string | null {
  if (!type || type === 'none') return null;
  return CONSULTANT_TYPES.find((t) => t.value === type)?.label ?? type;
}

export const STATUS_LABELS: Record<FirmMemberStatus, string> = {
  invited: 'Invited, not yet joined',
  inactive: 'Inactive',
  active: 'Active',
};

const BASE = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd`;

type Company = { id: string; name: string };

function rolesSet(c: Consultant): Set<string> {
  return new Set(Array.isArray(c.roles) ? c.roles : [c.roles as unknown as string].filter(Boolean));
}

export function FirmRoleManager({ firmId, accessToken, currentUser }: FirmRoleManagerProps) {
  const [consultants, setConsultants] = useState<Consultant[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [roleChanges, setRoleChanges] = useState<Map<string, Set<string>>>(new Map());
  const [hasChanges, setHasChanges] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  // Add person
  const [addOpen, setAddOpen] = useState(false);
  const [addName, setAddName] = useState('');
  const [addEmail, setAddEmail] = useState('');
  const [addRole, setAddRole] = useState('consultant');
  const [addType, setAddType] = useState('none');
  const [addCompanies, setAddCompanies] = useState<string[]>([]);
  const [addSetPassword, setAddSetPassword] = useState(false);
  const [addPassword, setAddPassword] = useState('');
  const [addError, setAddError] = useState('');
  const [adding, setAdding] = useState(false);

  // Edit person
  const [editing, setEditing] = useState<Consultant | null>(null);
  const [editName, setEditName] = useState('');
  const [editType, setEditType] = useState('none');
  const [editCompanies, setEditCompanies] = useState<string[]>([]);
  const [editStatus, setEditStatus] = useState<'active' | 'inactive'>('active');
  const [editError, setEditError] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => {
    loadConsultants();
    loadCompanies();
  }, [firmId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadCompanies() {
    try {
      const response = await fetch(`${BASE}/consultant/companies`, { headers: getAuthHeaders(accessToken) });
      if (!response.ok) return;
      const data = await response.json();
      const list: any[] = Array.isArray(data) ? data : data?.companies || [];
      setCompanies(list.filter((c) => c && c.id).map((c) => ({ id: String(c.id), name: c.name || c.id })).sort((a, b) => a.name.localeCompare(b.name)));
    } catch (error) {
      console.error('Error loading the firm\'s clients:', error);
    }
  }

  async function loadConsultants() {
    try {
      setLoading(true);
      const response = await fetch(
        `${BASE}/firms/${firmId}/users`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        // GET /firms/:firmId/users already returns the firm's members only (firmUsersCore.belongsToFirm),
        // shaped without the firm id fields; filtering on those here hid everyone (walk finding F1 / S8).
        const consultantUsers: Consultant[] = data.users || [];
        
        setConsultants(consultantUsers);
        
        // Initialize role changes map
        const initialChanges = new Map();
        consultantUsers.forEach((consultant: Consultant) => {
          initialChanges.set(consultant.id, rolesSet(consultant));
        });
        setRoleChanges(initialChanges);
        setHasChanges(false);
      } else {
        const errorText = await response.text();
        console.error('Failed to load consultants:', errorText);
        toast.error('Failed to load consultants');
      }
    } catch (error) {
      console.error('Error loading consultants:', error);
      toast.error('Error loading consultants');
    } finally {
      setLoading(false);
    }
  }

  function toggleRole(consultantId: string, role: string) {
    const newChanges = new Map(roleChanges);
    const userRoles = new Set(newChanges.get(consultantId) || new Set());
    
    if (userRoles.has(role)) {
      userRoles.delete(role);
    } else {
      userRoles.add(role);
    }
    
    newChanges.set(consultantId, userRoles);
    setRoleChanges(newChanges);
    
    // Check if there are any changes from original
    const hasAnyChanges = Array.from(newChanges.entries()).some(([id, roles]) => {
      const original = consultants.find(c => c.id === id);
      if (!original) return false;
      
      const originalRoles = rolesSet(original);
      
      // Compare sets
      if (roles.size !== originalRoles.size) return true;
      for (const role of roles) {
        if (!originalRoles.has(role)) return true;
      }
      return false;
    });
    
    setHasChanges(hasAnyChanges);
  }

  async function saveChanges() {
    try {
      setSaving(true);
      
      // Prepare updates for users whose roles changed
      const updates = [];
      for (const [userId, newRoles] of roleChanges.entries()) {
        const original = consultants.find(c => c.id === userId);
        if (!original) continue;
        
        const originalRoles = rolesSet(original);
        const hasChanged = newRoles.size !== originalRoles.size || 
          Array.from(newRoles).some(r => !originalRoles.has(r));
        
        if (hasChanged) {
          updates.push({
            userId,
            roles: Array.from(newRoles),
          });
        }
      }
      
      if (updates.length === 0) {
        toast.info('No changes to save');
        return;
      }
      
      // Send batch update to backend
      const response = await fetch(
        `${BASE}/firms/${firmId}/users/roles`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ updates }),
        }
      );
      
      if (response.ok) {
        toast.success(`Updated roles for ${updates.length} consultant(s)`);
        setHasChanges(false);
        await loadConsultants(); // Reload to get fresh data
      } else {
        const errorText = await response.text();
        console.error('Failed to update roles:', errorText);
        toast.error('Failed to update roles');
      }
    } catch (error) {
      console.error('Error saving role changes:', error);
      toast.error('Error saving role changes');
    } finally {
      setSaving(false);
    }
  }

  function resetChanges() {
    // Reset to original roles
    const originalChanges = new Map();
    consultants.forEach(consultant => {
      originalChanges.set(consultant.id, rolesSet(consultant));
    });
    setRoleChanges(originalChanges);
    setHasChanges(false);
  }

  // ---- add / edit / remove / resend --------------------------------------

  function openAdd() {
    setAddName(''); setAddEmail(''); setAddRole('consultant'); setAddType('none'); setAddCompanies([]);
    setAddSetPassword(false); setAddPassword(''); setAddError('');
    setAddOpen(true);
  }

  async function submitAdd() {
    setAddError('');
    if (!addName.trim() || !addEmail.trim()) { setAddError('Name and email are required'); return; }
    if (addSetPassword && addPassword.length < 8) { setAddError('A password must be at least 8 characters'); return; }
    setAdding(true);
    try {
      const response = await fetch(`${BASE}/firms/${firmId}/users`, {
        method: 'POST',
        headers: getAuthHeaders(accessToken),
        body: JSON.stringify({
          name: addName.trim(),
          email: addEmail.trim(),
          role: addRole,
          consultantType: addType === 'none' ? null : addType,
          assignedCompanies: addCompanies,
          ...(addSetPassword && addPassword ? { password: addPassword } : {}),
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || `Could not add the person (${response.status})`);
      if (addSetPassword) toast.success(`${addName.trim()} can sign in now. Share the password with them yourself.`);
      else if (data.emailSent) toast.success(`Invitation sent to ${addEmail.trim()}`);
      else toast.warning(`${addName.trim()} was added, but the invitation email could not be sent${data.emailError ? `: ${data.emailError}` : ''}. Use Resend invite once email is set up.`);
      setAddOpen(false);
      await loadConsultants();
    } catch (error: any) {
      setAddError(error?.message || 'Could not add the person');
    } finally {
      setAdding(false);
    }
  }

  function openEdit(c: Consultant) {
    setEditing(c);
    setEditName(c.name || '');
    setEditType(c.consultantType || 'none');
    setEditCompanies(Array.isArray(c.assignedCompanies) ? c.assignedCompanies : []);
    setEditStatus(c.status === 'inactive' ? 'inactive' : 'active');
    setEditError('');
  }

  async function submitEdit() {
    if (!editing) return;
    setEditError('');
    if (!editName.trim()) { setEditError('Name cannot be empty'); return; }
    setSavingEdit(true);
    try {
      const body: Record<string, unknown> = {
        name: editName.trim(),
        consultantType: editType === 'none' ? null : editType,
        assignedCompanies: editCompanies,
      };
      const wasInactive = editing.status === 'inactive';
      if ((editStatus === 'inactive') !== wasInactive) body.status = editStatus;
      const response = await fetch(`${BASE}/firms/${firmId}/users/${editing.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(accessToken),
        body: JSON.stringify(body),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || `Could not save (${response.status})`);
      toast.success(`${editName.trim()} saved`);
      setEditing(null);
      await loadConsultants();
    } catch (error: any) {
      setEditError(error?.message || 'Could not save the person');
    } finally {
      setSavingEdit(false);
    }
  }

  async function removePerson(c: Consultant) {
    const invited = c.status === 'invited';
    const question = invited
      ? `Revoke the invitation for ${c.name || c.email}? Their sign-in link stops working.`
      : `Remove ${c.name || c.email} from the firm? They lose access at once. This cannot be undone.`;
    if (!window.confirm(question)) return;
    setBusyId(c.id);
    try {
      const response = await fetch(`${BASE}/firms/${firmId}/users/${c.id}`, { method: 'DELETE', headers: getAuthHeaders(accessToken) });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || `Could not remove (${response.status})`);
      toast.success(invited ? 'Invitation revoked' : `${c.name || c.email} removed from the firm`);
      await loadConsultants();
    } catch (error: any) {
      toast.error(error?.message || 'Could not remove the person');
    } finally {
      setBusyId(null);
    }
  }

  async function resendInvite(c: Consultant) {
    setBusyId(c.id);
    try {
      const response = await fetch(`${BASE}/firms/${firmId}/users/${c.id}/resend-invite`, { method: 'POST', headers: getAuthHeaders(accessToken) });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || `Could not resend (${response.status})`);
      if (data.emailSent) toast.success(`Invitation resent to ${c.email}`);
      else toast.warning(`The invitation email could not be sent${data.emailError ? `: ${data.emailError}` : ''}`);
    } catch (error: any) {
      toast.error(error?.message || 'Could not resend the invitation');
    } finally {
      setBusyId(null);
    }
  }

  function toggleCompany(list: string[], set: (next: string[]) => void, id: string) {
    set(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  }

  const companyPicker = (list: string[], set: (next: string[]) => void, prefix: string) => (
    <div className="border rounded-lg p-3 space-y-2 max-h-48 overflow-y-auto">
      {companies.length === 0 ? (
        <p className="text-muted-foreground">No client companies yet</p>
      ) : companies.map((company) => (
        <div key={company.id} className="flex items-center gap-2">
          <Checkbox id={`${prefix}-${company.id}`} checked={list.includes(company.id)} onCheckedChange={() => toggleCompany(list, set, company.id)} />
          <Label htmlFor={`${prefix}-${company.id}`} className="cursor-pointer flex-1">{company.name}</Label>
        </div>
      ))}
    </div>
  );

  const typePicker = (value: string, set: (v: string) => void, id: string) => (
    <>
      <Select value={value} onValueChange={set}>
        <SelectTrigger id={id}><SelectValue placeholder="Specialization" /></SelectTrigger>
        <SelectContent>
          {CONSULTANT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
        </SelectContent>
      </Select>
      <p className="text-muted-foreground">{CONSULTANT_TYPES.find((t) => t.value === value)?.blurb}</p>
    </>
  );

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 flex items-center justify-center">
          <Loader2 className="size-8 animate-spin text-gray-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Shield className="size-5 text-purple-500" />
              Firm team
            </CardTitle>
            <CardDescription>
              Everyone in your firm, their roles, specialization and the clients they work with
            </CardDescription>
          </div>
          <div className="flex gap-2">
            {hasChanges && (
              <>
                <Button variant="outline" onClick={resetChanges} disabled={saving}>
                  Cancel
                </Button>
                <Button onClick={saveChanges} disabled={saving}>
                  {saving ? (
                    <>
                      <Loader2 className="size-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="size-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </>
            )}
            <Button variant={hasChanges ? 'outline' : 'default'} onClick={openAdd}><UserPlus className="size-4 mr-2" aria-hidden="true" />Add person</Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {hasChanges && (
          <Alert>
            <AlertTriangle className="size-4" />
            <AlertDescription>
              You have unsaved changes. Click "Save Changes" to apply role updates.
            </AlertDescription>
          </Alert>
        )}

        {consultants.length === 0 ? (
          <div className="text-center py-12">
            <Users className="size-12 mx-auto mb-3 text-gray-400" />
            <p className="text-muted-foreground">No consultants found in your firm</p>
          </div>
        ) : (
          <div className="border rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Person</TableHead>
                  <TableHead>Specialization</TableHead>
                  <TableHead className="text-center">Clients</TableHead>
                  {CONSULTANT_ROLES.map(role => (
                    <TableHead key={role.value} className="text-center">
                      <div className="space-y-1">
                        <div>{role.label}</div>
                        <div className="text-xs font-normal text-muted-foreground">
                          {role.description}
                        </div>
                      </div>
                    </TableHead>
                  ))}
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {consultants.map(consultant => {
                  const userRoles = roleChanges.get(consultant.id) || new Set();
                  const isCurrentUser = consultant.id === currentUser.id;
                  const status: FirmMemberStatus = consultant.status || 'active';
                  const typeLabel = consultantTypeLabel(consultant.consultantType);
                  const clientCount = Array.isArray(consultant.assignedCompanies) ? consultant.assignedCompanies.length : 0;
                  
                  return (
                    <TableRow key={consultant.id} className={status === 'inactive' ? 'opacity-60' : undefined}>
                      <TableCell>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium">{consultant.name || consultant.email}</span>
                            {isCurrentUser && (
                              <Badge variant="outline" className="text-xs">You</Badge>
                            )}
                            {status === 'invited' && <Badge variant="secondary" className="text-xs">{STATUS_LABELS.invited}</Badge>}
                            {status === 'inactive' && <Badge variant="outline" className="text-xs border-red-300 text-red-700">{STATUS_LABELS.inactive}</Badge>}
                          </div>
                          <div className="text-sm text-muted-foreground">{consultant.email}</div>
                        </div>
                      </TableCell>
                      <TableCell>{typeLabel ? <Badge variant="outline">{typeLabel}</Badge> : <span className="text-muted-foreground">—</span>}</TableCell>
                      <TableCell className="text-center" title={companies.filter((c) => consultant.assignedCompanies?.includes(c.id)).map((c) => c.name).join(', ') || undefined}>
                        {clientCount > 0 ? <Badge variant="secondary">{clientCount}</Badge> : <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      {CONSULTANT_ROLES.map(role => {
                        const hasRole = userRoles.has(role.value);
                        const isDisabled = isCurrentUser && role.value === 'cfAdmin'; // Can't remove own CF Admin role
                        
                        return (
                          <TableCell key={role.value} className="text-center">
                            <div className="flex items-center justify-center">
                              <Checkbox
                                checked={hasRole}
                                onCheckedChange={() => !isDisabled && toggleRole(consultant.id, role.value)}
                                disabled={isDisabled}
                                aria-label={`Toggle ${role.label} for ${consultant.name || consultant.email}`}
                              />
                            </div>
                          </TableCell>
                        );
                      })}
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {status === 'invited' && (
                            <Button variant="ghost" size="sm" onClick={() => resendInvite(consultant)} disabled={busyId === consultant.id} aria-label={`Resend invite to ${consultant.email}`} title="Resend invite">
                              {busyId === consultant.id ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <Mail className="size-4" aria-hidden="true" />}
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => openEdit(consultant)} aria-label={`Edit ${consultant.name || consultant.email}`} title="Edit">
                            <Pencil className="size-4" aria-hidden="true" />
                          </Button>
                          {!isCurrentUser && (
                            <Button variant="ghost" size="sm" onClick={() => removePerson(consultant)} disabled={busyId === consultant.id} aria-label={`${status === 'invited' ? 'Revoke invitation for' : 'Remove'} ${consultant.name || consultant.email}`} title={status === 'invited' ? 'Revoke invitation' : 'Remove from firm'}>
                              <Trash2 className="size-4 text-red-600" aria-hidden="true" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Role Descriptions */}
        <div className="mt-6 p-4 bg-muted rounded-lg">
          <h4 className="font-medium mb-3 flex items-center gap-2">
            <Shield className="size-4" />
            Role Permissions
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {CONSULTANT_ROLES.map(role => (
              <div key={role.value} className="flex items-start gap-2">
                <CheckCircle2 className="size-4 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-medium text-sm">{role.label}</div>
                  <div className="text-xs text-muted-foreground">{role.description}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>

      {/* Add person */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add a person to the firm</DialogTitle>
            <DialogDescription>They get an email with a link to set their password, or you set one now and share it yourself.</DialogDescription>
          </DialogHeader>
          {addError && <Alert variant="destructive"><AlertDescription>{addError}</AlertDescription></Alert>}
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="add-name">Full name</Label>
                <Input id="add-name" value={addName} onChange={(e) => setAddName(e.target.value)} placeholder="Sam Rivera" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="add-email">Email</Label>
                <Input id="add-email" type="email" value={addEmail} onChange={(e) => setAddEmail(e.target.value)} placeholder="sam@yourfirm.com" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="add-role">Role</Label>
              <Select value={addRole} onValueChange={setAddRole}>
                <SelectTrigger id="add-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CONSULTANT_ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-muted-foreground">{CONSULTANT_ROLES.find((r) => r.value === addRole)?.description}. More roles can be ticked on the list afterwards.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="add-type">Specialization</Label>
              {typePicker(addType, setAddType, 'add-type')}
            </div>
            <div className="space-y-2">
              <Label>Assigned clients</Label>
              <p className="text-muted-foreground">The client companies this person works with</p>
              {companyPicker(addCompanies, setAddCompanies, 'add-company')}
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Checkbox id="add-set-password" checked={addSetPassword} onCheckedChange={(v) => setAddSetPassword(v === true)} />
                <Label htmlFor="add-set-password" className="cursor-pointer">Set a password now instead of sending an invitation</Label>
              </div>
              {addSetPassword && (
                <PasswordInput id="add-password" value={addPassword} onChange={(e) => setAddPassword(e.target.value)} placeholder="At least 8 characters" autoComplete="new-password" />
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} disabled={adding}>Cancel</Button>
            <Button onClick={submitAdd} disabled={adding}>
              {adding ? <Loader2 className="size-4 mr-2 animate-spin" aria-hidden="true" /> : <UserPlus className="size-4 mr-2" aria-hidden="true" />}
              {addSetPassword ? 'Add person' : 'Send invitation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit person */}
      <Dialog open={!!editing} onOpenChange={(open) => { if (!open) setEditing(null); }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit {editing?.name || editing?.email}</DialogTitle>
            <DialogDescription>{editing?.email}. Roles are ticked on the list.</DialogDescription>
          </DialogHeader>
          {editError && <Alert variant="destructive"><AlertDescription>{editError}</AlertDescription></Alert>}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Full name</Label>
              <Input id="edit-name" value={editName} onChange={(e) => setEditName(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-type">Specialization</Label>
              {typePicker(editType, setEditType, 'edit-type')}
            </div>
            <div className="space-y-2">
              <Label>Assigned clients</Label>
              {companyPicker(editCompanies, setEditCompanies, 'edit-company')}
            </div>
            {editing && editing.id !== currentUser.id && editing.status !== 'invited' && (
              <div className="space-y-2">
                <Label htmlFor="edit-status">Standing</Label>
                <Select value={editStatus} onValueChange={(v) => setEditStatus(v as 'active' | 'inactive')}>
                  <SelectTrigger id="edit-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive (cannot sign in)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={savingEdit}>Cancel</Button>
            <Button onClick={submitEdit} disabled={savingEdit}>
              {savingEdit ? <Loader2 className="size-4 mr-2 animate-spin" aria-hidden="true" /> : <Save className="size-4 mr-2" aria-hidden="true" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
